import { useCanvasStore } from '../stores/useCanvasStore'
import { useOrderStore } from '../stores/useOrderStore'
import { useDecoDataStore } from '../stores/useDecoDataStore'
import { useAddDeco } from './useAddDeco'
import { loadImage } from './useImageLoader'
import { FONT_LIST } from '../utils/fontDefinitions'

/**
 * デフォルトデザイングループのキャンバスへの配置を担う。
 * 初期マウント時 (App.vue) と、デザイン切り替えパネル (DesignPanel) の双方から使用する。
 *
 * 「1つだけ表示・切り替えで差し替え」方式:
 *   - applySelectedDesign() は前グループ由来 (fromDesign=true) の配置物を消してから
 *     現在選択中のグループを配置する。ユーザーが自分で追加した要素は残す。
 *   - デザイン由来オブジェクトには fromDesign=true を付与して識別する。
 */
export function useApplyDesign() {
    const canvasStore = useCanvasStore()
    const orderStore = useOrderStore()
    const decoDataStore = useDecoDataStore()

    // decoId から画像URLを探す
    function findDecoImage(decoId) {
        const data = window.__DECO_DATA__ ?? {}
        const storageUrl = data.storageUrl ?? '/api/deco/storage/'
        // 1) サーバが解決したデフォルトデザイン用 deco 画像マップ (プロジェクト外フレーム/スタンプ含む)
        const map = data.decoImages ?? {}
        if (map[decoId]) return { url: storageUrl + map[decoId] }
        // 2) フォールバック: drink で利用可能なデコ一覧から探す
        const allGroups = [
            ...(data.frames?.styles ?? []),
            ...(data.frames?.events ?? []),
            ...(data.frames?.genres ?? []),
            ...(data.stamps?.styles ?? []),
            ...(data.stamps?.events ?? []),
            ...(data.stamps?.genres ?? []),
        ]
        for (const group of allGroups) {
            for (const deco of (group.decos ?? [])) {
                if (deco.id === decoId) {
                    return { url: storageUrl + deco.image_url }
                }
            }
        }
        return null
    }

    async function applyDesignGroup(group) {
        if (!group) return
        const { addFrame } = useAddDeco()

        // フレーム
        // 画像読み込み失敗 (ファイル欠損・404 等) でも後続のスタンプ/テキスト配置を
        // 止めないよう、フレームごとに try/catch でガードする。
        for (const f of (group.frames ?? [])) {
            if (!f.decoId) continue
            const decoImage = findDecoImage(f.decoId)
            if (!decoImage) continue
            try {
                await addFrame(decoImage.url, f.decoId)
            } catch (e) {
                console.warn('defaultDesign: frame load failed', f.decoId, e)
                continue
            }
            // 直近に追加された frame オブジェクトに編集制限フラグを反映
            const last = canvasStore.objects[canvasStore.objects.length - 1]
            if (last && last.objectType === 'frame' && last.decoId === f.decoId) {
                Object.assign(last, {
                    fromDesign: true,
                    movable:  f.movable  !== undefined ? !!f.movable  : false, // frame は既存仕様で固定
                    scalable: f.scalable !== undefined ? !!f.scalable : false,
                })
            }
        }

        // スタンプ
        for (const s of (group.stamps ?? [])) {
            if (!s.decoId) continue
            const decoImage = findDecoImage(s.decoId)
            if (!decoImage) continue
            try {
                const img = await loadImage(decoImage.url)
                const ratio = img.naturalWidth / img.naturalHeight
                const max = canvasStore.canvasSize * 0.3
                const w = ratio >= 1 ? max : max * ratio
                const h = ratio >= 1 ? max / ratio : max
                canvasStore.addObject({
                    objectType: 'stamp',
                    src: decoImage.url,
                    x: s.x ?? canvasStore.labelCenterX,
                    y: s.y ?? canvasStore.labelCenterY,
                    width: w,
                    height: h,
                    rotation: 0,
                    decoId: s.decoId,
                    fromDesign: true,
                    movable:  s.movable  !== undefined ? !!s.movable  : true,
                    scalable: s.scalable !== undefined ? !!s.scalable : true,
                })
                orderStore.incrementDecoCount(s.decoId)
            } catch (e) {
                console.warn('defaultDesign: stamp load failed', s.decoId, e)
            }
        }

        // テキスト
        for (const t of (group.texts ?? [])) {
            if (!t.content && !t.defaultContent) continue
            // font フィールドは ID (例: 'notoSansJP') または CSS family 名どちらでも受け付ける
            const fontEntry = FONT_LIST.find(f => f.id === t.font)
            const fontFamily = fontEntry ? fontEntry.family : (t.font ?? FONT_LIST[0].family)
            const fontWeight = fontEntry ? fontEntry.weight : ''
            // box が指定されてれば中心配置、なければ x/y を直接使用
            const box = t.box
            const x = box ? (box.x + box.width  / 2) : (t.x ?? canvasStore.labelCenterX)
            const y = box ? (box.y + box.height / 2) : (t.y ?? canvasStore.labelCenterY)
            canvasStore.addObject({
                objectType: 'text',
                content:   t.content ?? t.defaultContent ?? '',
                font:      fontFamily,
                weight:    fontWeight,
                color:     t.color     ?? '#000000',
                fontSize:  t.fontSize  ?? 40,
                direction: t.direction ?? 'horizontal',
                width:  200,
                height:  60,
                rotation: 0,
                x,
                y,
                fromDesign:   true,
                slotKey:      t.slotKey,
                lockPosition: !!t.lockPosition,
                lockResize:   !!t.lockResize,
                lockContent:  !!t.lockContent,
                lockFont:     !!t.lockFont,
                autoShrink: t.autoShrink && t.autoShrink.enabled && t.autoShrink.box
                    ? { enabled: true, box: t.autoShrink.box, fontSizeMin: t.autoShrink.fontSizeMin ?? 12 }
                    : null,
            })
        }
    }

    /**
     * 現在選択中 (decoDataStore.selectedDesignGroup) のグループを配置する。
     * 配置前に前グループ由来のオブジェクト・deco カウントを取り除く。
     */
    async function applySelectedDesign() {
        // 前グループ由来オブジェクトの deco カウントを戻してから一括削除
        for (const obj of canvasStore.objects) {
            if (obj.fromDesign === true && obj.decoId) {
                orderStore.decrementDecoCount(obj.decoId)
            }
        }
        canvasStore.clearDesignObjects()

        await applyDesignGroup(decoDataStore.selectedDesignGroup)
    }

    /**
     * index のグループに切り替えて配置し直す。
     */
    async function selectDesign(index) {
        if (index === decoDataStore.selectedDesignIndex
            && canvasStore.objects.some(o => o.fromDesign)) {
            return // 同じグループが既に配置済みなら何もしない
        }
        decoDataStore.selectDesign(index)
        await applySelectedDesign()
    }

    return { applyDesignGroup, applySelectedDesign, selectDesign }
}
